/*
PURPOSE: Editorial twin of CollectiveImpactCalculator — identical maths, copy
and analytics to the legacy calculator, in the editorial warm card treatment
with a slider.
USE CASES:
- The "CORAL DYNAMICS" / impact section of Home and PublicArtist.
DOMAIN BOUNDARIES:
- Maths/constants come from the shared impactMath module; copy comes verbatim
  from useCopy().publicPages.collectiveImpactCalculator; analytics fire the
  same debounced COLLECTIVE_CALCULATOR_INTERACTION as legacy. None are
  duplicated or re-derived here, so the two calculators cannot drift.
- The typed input keeps the legacy string/0-floor semantics (empty -> 0,
  parseFloat, Math.max(0, …)) so edge cases stay identical to production.
  The slider is an additional editorial control bound to the same state
  (clamped 1–70 for the track only); the underlying value/maths are unchanged.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { Users } from 'lucide-react';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import debounce from 'lodash/debounce';
import { useCopy } from '../../../hooks/useCopy';
import {
  IMPACT_ARTIST_COUNT,
  IMPACT_PARTICIPANT_COUNT,
  computePerArtistImpact,
  artistShareOf,
} from '../../../components/CollectiveImpactTable/impactMath';

const SLIDER_MIN = 1;
const SLIDER_MAX = 70;

const EditorialImpactCalculator: React.FC = () => {
  const copy = useCopy().publicPages.collectiveImpactCalculator;
  const [contributionInput, setContributionInput] = useState('5');
  const [contribution, setContribution] = useState(5);
  const artistCount = IMPACT_ARTIST_COUNT;
  const participantCount = IMPACT_PARTICIPANT_COUNT;
  const [perArtistImpact, setPerArtistImpact] = useState(0);

  useEffect(() => {
    setPerArtistImpact(computePerArtistImpact(contribution));
  }, [contribution]);

  const debouncedTrackEvent = useMemo(
    () =>
      debounce((value: number) => {
        if (value > 0) {
          trackEvent(EventName.COLLECTIVE_CALCULATOR_INTERACTION, { contribution: value });
        }
      }, 1250),
    []
  );

  const applyValue = (rawValue: string, numericValue: number) => {
    setContributionInput(rawValue);
    setContribution(numericValue);
    debouncedTrackEvent(numericValue);
  };

  const handleContributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value === '' ? 0 : Math.max(0, parseFloat(value));
    applyValue(value, numericValue);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseInt(e.target.value, 10);
    applyValue(String(numericValue), numericValue);
  };

  const sliderValue = Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, contribution || SLIDER_MIN));

  return (
    <div className="bg-white rounded-3xl border border-editorial-muted/10 shadow-[0_24px_48px_-24px_rgba(12,19,45,0.12)] p-7 md:p-9 w-full">
      <span className="block text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60">
        {copy.contributionLabel}
      </span>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-cormorant text-editorial-muted text-4xl md:text-5xl tabular-nums leading-none">
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={contributionInput}
          onChange={handleContributionChange}
          aria-label={copy.contributionLabel}
          className="font-cormorant text-editorial-muted text-5xl md:text-6xl tabular-nums leading-none bg-transparent w-28 md:w-36 focus:outline-none border-b border-editorial-muted/20 focus:border-editorial-muted/60 transition-colors"
        />
        <span className="text-editorial-muted/55 text-base md:text-lg ml-1">/mo</span>
      </div>

      <input
        type="range"
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        step={1}
        value={sliderValue}
        onChange={handleSliderChange}
        aria-label={copy.contributionLabel}
        className="mt-5 w-full accent-coral-orange"
      />

      <p className="mt-5 text-editorial-muted/75 text-base leading-relaxed">
        {copy.perArtistLine(artistCount, contribution > 0 ? (artistShareOf(contribution) / artistCount).toFixed(2) : '0.00')}
      </p>

      <div className="mt-7 pt-7 border-t border-editorial-muted/10">
        <div className="flex items-center gap-3 text-editorial-muted/75 text-sm md:text-base">
          <Users size={18} className="text-coral-orange" strokeWidth={2} />
          <span>{copy.participantsLine(participantCount.toLocaleString())}</span>
        </div>

        <div className="mt-4 flex items-baseline gap-3">
          <span className="font-cormorant text-editorial-muted text-4xl md:text-5xl tabular-nums leading-none">
            ${perArtistImpact.toFixed(2)}
          </span>
          <span className="font-cormorant text-editorial-muted/70 text-lg md:text-xl">
            {copy.perArtistEachMonth}
          </span>
        </div>

        <p className="mt-4 text-editorial-muted/55 text-sm italic">{copy.scaleNote}</p>
      </div>
    </div>
  );
};

export default EditorialImpactCalculator;
